import {
	type CreateTokenDetailsFormData,
	CreateTokenDetailsSchema,
	TokenCategorySchema,
} from "@repo/shared-types";
import { useState } from "react";
import { ZodError } from "zod";

import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Textarea } from "../ui/Textarea";
import { LogoUpload } from "./LogoUpload";
import { SocialLinksSection } from "./SocialLinksSection";

type DetailsStepProps = {
	data: Partial<CreateTokenDetailsFormData>;
	onDataChange: (data: Partial<CreateTokenDetailsFormData>) => void;
	onNext: () => void;
	uploadFn: (file: File) => Promise<{ data: { id: string; url: string } }>;
	logoPreviewUrl: string | null;
	onLogoPreviewChange: (url: string | null) => void;
};

type ValidationErrors = Partial<
	Record<
		| keyof CreateTokenDetailsFormData
		| "socialLinks.website"
		| "socialLinks.twitter"
		| "socialLinks.discord"
		| "socialLinks.telegram",
		string
	>
>;

const categoryOptions = [
	{ value: "AI", label: "AI & Machine Learning" },
	{ value: "DeFi", label: "DeFi & Finance" },
	{ value: "Gaming", label: "Gaming & Metaverse" },
	{ value: "Creator", label: "Creator Economy" },
	{ value: "Meme", label: "Meme & Community" },
	{ value: "Social", label: "Social & Communication" },
	{ value: "Infrastructure", label: "Infrastructure & Tools" },
	{ value: "Other", label: "Other" },
];

function validateField(
	field: keyof CreateTokenDetailsFormData,
	value: unknown
): string | undefined {
	try {
		const fieldSchema = CreateTokenDetailsSchema.shape[field];
		fieldSchema.parse(value);
		return undefined;
	} catch (error) {
		if (error instanceof ZodError && error.issues.length > 0) {
			return error.issues[0]?.message ?? "Invalid value";
		}
		if (error instanceof Error) {
			return error.message;
		}
		return "Invalid value";
	}
}

function collectErrors(data: Partial<CreateTokenDetailsFormData>): ValidationErrors {
	const requiredFields: Array<{ key: keyof CreateTokenDetailsFormData; msg: string }> = [
		{ key: "name", msg: "Name is required" },
		{ key: "symbol", msg: "Symbol is required" },
		{ key: "description", msg: "Description is required" },
		{ key: "category", msg: "Category is required" },
	];
	const newErrors: ValidationErrors = {};
	for (const { key, msg } of requiredFields) {
		if (!data[key]) {
			newErrors[key] = msg;
		} else {
			const err = validateField(key, data[key]);
			if (err) newErrors[key] = err;
		}
	}
	if (data.tagline) {
		const err = validateField("tagline", data.tagline);
		if (err) newErrors.tagline = err;
	}
	return newErrors;
}

export function DetailsStep({
	data,
	onDataChange,
	onNext,
	uploadFn,
	logoPreviewUrl,
	onLogoPreviewChange,
}: DetailsStepProps) {
	const [errors, setErrors] = useState<ValidationErrors>({});

	const updateData = (field: keyof CreateTokenDetailsFormData, value: unknown) => {
		const newData = { ...data, [field]: value };
		onDataChange(newData);

		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: undefined }));
		}
	};

	const handleSocialLinkChange = (
		platform: keyof NonNullable<CreateTokenDetailsFormData["socialLinks"]>,
		value: string
	) => {
		const socialLinks = data.socialLinks || {};
		const newSocialLinks = { ...socialLinks, [platform]: value || undefined };
		updateData("socialLinks", newSocialLinks);

		const errorKey: keyof ValidationErrors = `socialLinks.${platform}`;
		if (errors[errorKey]) {
			setErrors((prev) => ({ ...prev, [errorKey]: undefined }));
		}
	};

	const handleBlur = (field: keyof CreateTokenDetailsFormData) => {
		const error = validateField(field, data[field]);
		if (error) {
			setErrors((prev) => ({ ...prev, [field]: error }));
		}
	};

	const handleNext = () => {
		const result = CreateTokenDetailsSchema.safeParse(data);
		if (result.success) {
			onNext();
		} else {
			setErrors(collectErrors(data));
		}
	};

	const handleUploadComplete = (uploadId: string, _url: string) => {
		updateData("logoUploadId", uploadId);
		// Preview URL is set via onPreviewChange from LogoUpload (blob URL)
	};

	const handleLogoRemove = () => {
		updateData("logoUploadId", undefined);
		onLogoPreviewChange(null);
	};

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-2xl font-bold text-pastel-black mb-2">Token Details</h2>
				<p className="text-pastel-gray">Configure your token's basic information and identity.</p>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div className="space-y-6">
					<Input
						label="Token Name"
						placeholder="Enter token name"
						value={data.name || ""}
						onChange={(e) => updateData("name", e.target.value)}
						onBlur={() => handleBlur("name")}
						error={errors.name || ""}
						maxLength={50}
					/>

					<Input
						label="Symbol"
						placeholder="TOKEN"
						value={data.symbol || ""}
						onChange={(e) => updateData("symbol", e.target.value.toUpperCase())}
						onBlur={() => handleBlur("symbol")}
						error={errors.symbol || ""}
						maxLength={10}
					/>

					<Input
						label="Tagline (Optional)"
						placeholder="Short catchy description"
						value={data.tagline || ""}
						onChange={(e) => updateData("tagline", e.target.value)}
						onBlur={() => handleBlur("tagline")}
						error={errors.tagline || ""}
						maxLength={100}
					/>

					<Textarea
						label="Description"
						placeholder="Describe your token's purpose and utility..."
						value={data.description || ""}
						onChange={(e) => updateData("description", e.target.value)}
						onBlur={() => handleBlur("description")}
						error={errors.description || ""}
						rows={4}
						maxLength={500}
					/>

					<Select
						label="Category"
						options={categoryOptions}
						value={data.category || ""}
						onChange={(e) => {
							const parsed = TokenCategorySchema.safeParse(e.target.value);
							if (parsed.success) updateData("category", parsed.data);
						}}
						onBlur={() => handleBlur("category")}
						error={errors.category || ""}
						placeholder="Select category"
					/>
				</div>

				<div className="space-y-6">
					<div>
						<span className="block text-sm font-medium text-pastel-black mb-2">Logo</span>
						<LogoUpload
							onUploadComplete={handleUploadComplete}
							onRemove={handleLogoRemove}
							uploadFn={uploadFn}
							previewUrl={logoPreviewUrl}
							onPreviewChange={onLogoPreviewChange}
						/>
					</div>

					<SocialLinksSection
						socialLinks={data.socialLinks || {}}
						errors={errors}
						onSocialLinkChange={handleSocialLinkChange}
					/>
				</div>
			</div>

			<div className="flex justify-end pt-6">
				<Button onClick={handleNext} size="lg">
					Next Step
				</Button>
			</div>
		</div>
	);
}
